import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutGrid, Settings,
  Layers, FileText, Database,
  MessageSquare, BookOpen, Users, Home,
} from 'lucide-react'
import { Logo } from '@/components/brand/Logo'
import { Badge } from '@/components/ui/Badge'
import { cn } from '@/lib/utils'
import { useState, useEffect } from 'react'
import { useSession } from '@/lib/session/SessionContext'
import type React from 'react'

const DEMO_PROJECT_ID = 'demo'

type NavItem = {
  label: string
  href: string
  Icon: React.ComponentType<{ className?: string; strokeWidth?: string | number }>
  iconColor: string
}

const workspaceNavBase: Omit<NavItem, 'href'>[] = [
  { label: 'Projects', Icon: LayoutGrid,    iconColor: 'text-echo-mark'       },
  { label: 'Ask',      Icon: MessageSquare, iconColor: 'text-echo-indigo'     },
  { label: 'Reports',  Icon: FileText,      iconColor: 'text-citation-indigo' },
  { label: 'Library',  Icon: BookOpen,      iconColor: 'text-echo-indigo'     },
  { label: 'Settings', Icon: Settings,      iconColor: 'text-echo-ink'        },
]

export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { session } = useSession()

  const isProjectRoute = location.pathname.includes('/app/projects/')
  // Extract active project ID from URL (e.g. /app/projects/{id}/echo-panel)
  const activeProjectId = isProjectRoute
    ? (location.pathname.match(/\/app\/projects\/([^/]+)/)?.[1] ?? DEMO_PROJECT_ID)
    : DEMO_PROJECT_ID

  const isDemoRoute = location.pathname === '/app/demo' || (isProjectRoute && activeProjectId === DEMO_PROJECT_ID)
  const isDemoMode = !session || isDemoRoute

  const uploadHref = session
    ? (isProjectRoute && activeProjectId !== DEMO_PROJECT_ID ? `/app/projects/${activeProjectId}/upload` : '/app/upload')
    : '/app/upload'

  // Fetch real project name for breadcrumb when on a real project route
  const [projectName, setProjectName] = useState<string | null>(null)
  // First real project ID — used for "Back to Live" navigation from demo mode
  const [firstProjectId, setFirstProjectId] = useState<string | null>(null)

  useEffect(() => {
    if (!session) return
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<Array<{ project_id: string }>> : [])
      .then(projects => { if (projects[0]) setFirstProjectId(projects[0].project_id) })
      .catch(() => {})
  }, [session])

  useEffect(() => {
    if (!isProjectRoute || activeProjectId === DEMO_PROJECT_ID || !session) {
      setProjectName(null)
      return
    }
    const token = localStorage.getItem('echo_session')
    if (!token) return
    fetch(`/api/projects/${activeProjectId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() as Promise<{ name: string }> : null)
      .then(p => { if (p?.name) setProjectName(p.name) })
      .catch(() => {})
  }, [activeProjectId, isProjectRoute, session])

  const liveHref = session ? '/app/projects' : '/app/onboarding'

  const workspaceNav: NavItem[] = workspaceNavBase.map(n => ({
    ...n,
    href: n.label === 'Projects' ? (session ? '/app/projects' : '/app/demo')
      : n.label === 'Ask' ? (isProjectRoute && activeProjectId !== DEMO_PROJECT_ID ? `/app/projects/${activeProjectId}/ask` : '/app/projects')
      : n.label === 'Reports' ? (isProjectRoute && activeProjectId !== DEMO_PROJECT_ID ? `/app/projects/${activeProjectId}/report` : '/app/projects')
      : n.label === 'Library' ? '/app/library'
      : '/app/settings',
  }))

  const pid = activeProjectId
  const projectNav: NavItem[] = [
    { label: 'Home',       href: `/app/projects/${pid}`,            Icon: Home,     iconColor: 'text-echo-mark'       },
    { label: 'Ask',        href: `/app/projects/${pid}/ask`,        Icon: MessageSquare, iconColor: 'text-echo-indigo' },
    { label: 'Twin Panel', href: `/app/projects/${pid}/twin-panel`, Icon: Users,    iconColor: 'text-echo-indigo'     },
    { label: 'Pre-Read',   href: `/app/projects/${pid}/pre-read`,   Icon: Layers,   iconColor: 'text-echo-mark'       },
    { label: 'Report',     href: `/app/projects/${pid}/report`,     Icon: FileText, iconColor: 'text-citation-indigo' },
    { label: 'Data',       href: `/app/projects/${pid}/data`,       Icon: Database, iconColor: 'text-echo-ink'        },
  ]

  const mobileNav: NavItem[] = [
    { label: 'Projects', href: session ? '/app/projects' : '/app/demo', Icon: LayoutGrid,    iconColor: 'text-echo-mark' },
    { label: 'Home',     href: `/app/projects/${pid}`,                  Icon: Home,          iconColor: 'text-echo-mark' },
    { label: 'Ask',      href: `/app/projects/${pid}/ask`,              Icon: MessageSquare, iconColor: 'text-echo-indigo' },
    { label: 'Report',   href: `/app/projects/${pid}/report`,           Icon: FileText,      iconColor: 'text-citation-indigo' },
    { label: 'Settings', href: '/app/settings',                         Icon: Settings,      iconColor: 'text-echo-ink' },
  ]

  const NavLink = ({ href, label, Icon, iconColor }: NavItem) => {
    const isActive = location.pathname === href
    return (
      <Link
        to={href}
        className={cn(
          'flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors',
          isActive
            ? 'bg-soft-panel text-ink font-medium'
            : 'text-ink/70 hover:bg-soft-panel/60 hover:text-ink'
        )}
        onClick={() => setSidebarOpen(false)}
      >
        <Icon className={cn('w-5 h-5 shrink-0', iconColor)} strokeWidth={1.75} />
        {label}
      </Link>
    )
  }

  const Sidebar = () => (
    <div className="flex flex-col h-full">
      {/* Logo + workspace */}
      <div className="p-4 border-b border-border-gray">
        <Link to={session ? '/app/projects' : '/'} className="block mb-3">
          <Logo size="sm" />
        </Link>
        <div className="flex items-center gap-2 px-3 py-2 bg-soft-panel rounded-lg">
          <div className="w-6 h-6 bg-human-teal rounded text-white text-xs flex items-center justify-center font-medium shrink-0">
            {session?.email?.[0]?.toUpperCase() ?? 'D'}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-medium text-deep-ink truncate">
              {session ? (session.email.split('@')[1]?.split('.')[0] ?? 'My Workspace') : 'Demo Workspace'}
            </p>
            <p className="text-xs text-slate-400 truncate">{session?.email ?? 'demo mode'}</p>
          </div>
        </div>
      </div>

      {/* Mode toggle */}
      <div className="px-4 py-2.5 border-b border-border-gray">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-500">Mode</span>
          {isDemoMode ? (
            <div className="flex items-center gap-2">
              <Badge variant="demo" size="sm">Demo</Badge>
              {session && (
                <Link
                  to={liveHref}
                  className="text-xs text-human-teal hover:underline"
                  onClick={() => setSidebarOpen(false)}
                >
                  ← Live
                </Link>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Badge variant="success" size="sm">Live</Badge>
              <Link
                to="/app/demo"
                className="text-xs text-slate-400 hover:text-deep-ink"
                onClick={() => setSidebarOpen(false)}
              >
                View example
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Upload CTA */}
      {session && (
        <div className="px-4 py-3 border-b border-border-gray">
          <Link
            to={isProjectRoute && activeProjectId !== DEMO_PROJECT_ID ? `/app/projects/${activeProjectId}/upload` : '/app/upload'}
            className="flex items-center justify-center gap-1.5 w-full px-3 py-2 rounded-lg bg-echo-indigo text-white text-xs font-medium hover:bg-echo-indigo/90 transition-colors"
            onClick={() => setSidebarOpen(false)}
          >
            Import survey data
          </Link>
        </div>
      )}

      {/* Workspace nav */}
      <div className="px-3 py-3 space-y-0.5">
        {workspaceNav.map(n => <NavLink key={n.label} {...n} />)}
      </div>

      {/* Project nav */}
      {isProjectRoute && (
        <div className="px-3 py-3 border-t border-border-gray space-y-0.5">
          <div className="px-3 pt-1 pb-2">
            <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-ink/50">
              {activeProjectId === DEMO_PROJECT_ID
                ? 'Demo Project'
                : (projectName ?? `Project ${activeProjectId.slice(0, 8)}…`)}
            </p>
          </div>
          {projectNav.map(n => <NavLink key={n.label} {...n} />)}
        </div>
      )}

      {/* Bottom spacer */}
      <div className="mt-auto px-3 py-3 border-t border-border-gray">
        <Link
          to="/app/settings/api-keys"
          className="flex items-center gap-2 px-3 py-2 text-xs text-ink/40 hover:text-ink/70 rounded-lg hover:bg-soft-panel transition-colors"
          onClick={() => setSidebarOpen(false)}
        >
          API keys &amp; MCP
        </Link>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen flex bg-off-white">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 shrink-0 flex-col border-r border-border-gray bg-paper h-screen sticky top-0 overflow-y-auto">
        <Sidebar />
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-paper border-r border-border-gray h-full overflow-y-auto">
            <Sidebar />
          </div>
          <div className="flex-1 bg-deep-ink/40" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-14 border-b border-border-gray bg-paper px-4 flex items-center gap-3 sticky top-0 z-30">
          <button
            className="md:hidden p-1.5 rounded-lg hover:bg-soft-panel"
            onClick={() => setSidebarOpen(true)}
          >
            <div className="space-y-1.5 w-5">
              <span className="block h-0.5 bg-slate-600" />
              <span className="block h-0.5 bg-slate-600" />
              <span className="block h-0.5 bg-slate-600" />
            </div>
          </button>

          {/* Breadcrumb */}
          <div className="flex-1 flex items-center gap-2 text-sm min-w-0">
            <Link to={session ? '/app/projects' : '/app/demo'} className="text-slate-400 truncate hover:text-deep-ink transition-colors">
              {session ? (session.email.split('@')[1]?.split('.')[0] ?? 'My Workspace') : 'Demo Workspace'}
            </Link>
            {isProjectRoute && (
              <>
                <span className="text-border-gray">/</span>
                <span className="text-deep-ink font-medium truncate">
                  {activeProjectId === DEMO_PROJECT_ID
                    ? 'Demo Project'
                    : (projectName ?? activeProjectId.slice(0, 12) + '…')}
                </span>
              </>
            )}
          </div>

          {isDemoMode && <Badge variant="demo" size="sm">Demo data</Badge>}

          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-human-teal rounded-full text-white text-xs flex items-center justify-center font-medium">
              {session?.email?.[0]?.toUpperCase() ?? 'D'}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-x-hidden">
          <Outlet />
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-paper border-t border-border-gray z-40 flex">
        {mobileNav.map(item => {
          const isActive = location.pathname === item.href
          return (
            <Link
              key={item.label}
              to={item.href}
              className={cn(
                'flex-1 flex flex-col items-center py-2 gap-0.5 text-xs min-h-[44px] transition-colors',
                isActive ? 'text-echo-mark' : 'text-slate-400'
              )}
            >
              <item.Icon
                className={cn('w-5 h-5', isActive ? item.iconColor : 'text-slate-400')}
                strokeWidth={1.75}
              />
              <span>{item.label}</span>
            </Link>
          )
        })}
      </nav>
    </div>
  )
}
